/**
 * PAY-P1c — logique pure Admin pour paymentCollectionMode / depositAmount.
 * Pas de fetch, pas d’API, pas de recalcul quote serveur.
 */

export const PAYMENT_COLLECTION_MODES = Object.freeze(['NONE', 'DEPOSIT_FIXED', 'FULL']);

/** Options select — labels FR uniquement (jamais l’enum technique en UI). */
export const PAYMENT_COLLECTION_OPTIONS = Object.freeze([
  { value: 'NONE', label: 'Aucun paiement en ligne' },
  { value: 'DEPOSIT_FIXED', label: 'Acompte fixe' },
  { value: 'FULL', label: 'Paiement intégral' },
]);

export const PAYMENT_COLLECTION_SECTION_TITLE = 'Paiement à la réservation';

export const PAYMENT_COLLECTION_SECTION_HELP =
  'Montant demandé au client lors de la prise de rendez-vous en ligne.';

export const DEPOSIT_AMOUNT_LABEL = 'Montant de l’acompte (€)';

export const DEPOSIT_AMOUNT_HELP =
  'Montant fixe par réservation, quel que soit le nombre de participants.';

export const DEPOSIT_OVER_BASE_PRICE_WARNING =
  'L’acompte dépasse le prix de base. Le montant réellement demandé sera limité au total de la réservation.';

export const PAYMENT_WHEN_RESERVATION_OFFLINE_HELP =
  'La collecte s’appliquera lorsque la réservation en ligne sera activée.';

export const DEPOSIT_AMOUNT_INVALID_MESSAGE =
  'Indiquez un montant d’acompte supérieur à 0 €.';

/**
 * @param {unknown} value
 * @returns {'NONE'|'DEPOSIT_FIXED'|'FULL'}
 */
export function normalizePaymentCollectionMode(value) {
  const mode = String(value || 'NONE').toUpperCase();
  if (PAYMENT_COLLECTION_MODES.includes(mode)) {
    return /** @type {'NONE'|'DEPOSIT_FIXED'|'FULL'} */ (mode);
  }
  return 'NONE';
}

/** Création : défaut NONE + deposit vide. */
export function defaultPaymentCollectionForCreate() {
  return {
    paymentCollectionMode: 'NONE',
    depositAmount: '',
  };
}

/**
 * Hydratation édition depuis une prestation API (GET déjà exposé).
 * @param {{ paymentCollectionMode?: string|null, depositAmount?: number|null }|null|undefined} prestation
 */
export function hydratePaymentCollectionFromPrestation(prestation) {
  const mode = normalizePaymentCollectionMode(prestation?.paymentCollectionMode);
  const raw = prestation?.depositAmount;
  const depositAmount =
    raw === null || raw === undefined || raw === '' ? '' : String(raw);
  return {
    paymentCollectionMode: mode,
    depositAmount,
  };
}

/**
 * Parse montant € (accepte virgule). Round 2 décimales.
 * @param {unknown} value
 * @returns {number|null}
 */
export function parseDepositAmountInput(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n =
    typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(n)) {
    return null;
  }
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Changement de mode : reset deposit hors FIXED (évite fuite payload).
 * @param {string} nextMode
 * @param {unknown} previousDepositAmount
 */
export function applyPaymentCollectionModeChange(nextMode, previousDepositAmount = '') {
  const paymentCollectionMode = normalizePaymentCollectionMode(nextMode);
  if (paymentCollectionMode === 'DEPOSIT_FIXED') {
    return {
      paymentCollectionMode,
      depositAmount:
        previousDepositAmount === null || previousDepositAmount === undefined
          ? ''
          : previousDepositAmount,
    };
  }
  return {
    paymentCollectionMode,
    depositAmount: '',
  };
}

/**
 * Validation UX minimale avant save (backend = vérité).
 * @param {{ paymentCollectionMode?: string, depositAmount?: unknown }} form
 * @returns {{ valid: boolean, mode: string, depositAmount: number|null, error: string|null }}
 */
export function validatePaymentCollectionForm(form = {}) {
  const mode = normalizePaymentCollectionMode(form.paymentCollectionMode);

  if (mode !== 'DEPOSIT_FIXED') {
    return { valid: true, mode, depositAmount: null, error: null };
  }

  const parsed = parseDepositAmountInput(form.depositAmount);
  if (parsed === null || parsed <= 0) {
    return {
      valid: false,
      mode,
      depositAmount: null,
      error: DEPOSIT_AMOUNT_INVALID_MESSAGE,
    };
  }

  return { valid: true, mode, depositAmount: parsed, error: null };
}

/**
 * Champs payload POST/PUT — toujours les deux clés ; jamais '' ni string brute.
 * Ignore intentionnellement participants / options / prixPromo (pas de quote UI).
 * @param {{ paymentCollectionMode?: string, depositAmount?: unknown, participants?: unknown }} form
 * @returns {{ paymentCollectionMode: string, depositAmount: number|null }}
 */
export function buildPaymentCollectionPayload(form = {}) {
  const validation = validatePaymentCollectionForm(form);
  if (!validation.valid) {
    const err = new Error(validation.error || DEPOSIT_AMOUNT_INVALID_MESSAGE);
    err.code = 'DEPOSIT_AMOUNT_INVALID';
    throw err;
  }

  if (validation.mode === 'DEPOSIT_FIXED') {
    return {
      paymentCollectionMode: 'DEPOSIT_FIXED',
      depositAmount: validation.depositAmount,
    };
  }

  return {
    paymentCollectionMode: validation.mode,
    depositAmount: null,
  };
}

/**
 * Soft warning uniquement vs prix de base (jamais prixPromo).
 * @param {{ depositAmount?: unknown, prix?: unknown, paymentCollectionMode?: string }} args
 */
export function shouldWarnDepositOverBasePrice({
  depositAmount,
  prix,
  paymentCollectionMode,
} = {}) {
  if (normalizePaymentCollectionMode(paymentCollectionMode) !== 'DEPOSIT_FIXED') {
    return false;
  }
  const deposit = parseDepositAmountInput(depositAmount);
  const basePrice = parseDepositAmountInput(prix);
  if (deposit === null || basePrice === null || basePrice <= 0) {
    return false;
  }
  return deposit > basePrice;
}
